// Copyright (c) 2025 Annadaneshwar Yadawad. All rights reserved.

import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from '../components/Footer';
import './Login.css';

const Login = () => {
  const KEYCLOAK_BASE_URL =
    process.env.REACT_APP_KEYCLOAK_BASE_URL ||
    'https://keycloak-app.whitebush-8de3c8f4.centralindia.azurecontainerapps.io';
  const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || 'servantica';
  const KEYCLOAK_CLIENT_ID =
    process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'servantica';
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returnTo = params.get('returnTo');
  const [showCustomLogin, setShowCustomLogin] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      const from = returnTo || location.state?.from?.pathname || '/dashboard';
      navigate(from);
    }
  }, [isAuthenticated, navigate, location, returnTo]);

  const handleAuth0Google = () => {
    try {
      setShowCustomLogin(false);
      loginWithRedirect({
        authorizationParams: {
          connection: 'google-oauth2',
          redirect_uri: `${window.location.origin}/dashboard`
        },
        appState: {
          returnTo: returnTo || location.state?.from?.pathname || '/dashboard'
        }
      });
    } catch (error) {
      console.error('Google login error:', error);
      setError('Could not connect to Google. Please try again.');
      setShowCustomLogin(true);
    }
  };

  const handleKeycloakLogin = () => {
    setError('');
    try {
      const postLoginRedirect = returnTo || location.state?.from?.pathname || '/dashboard';
      sessionStorage.setItem('post_login_redirect', postLoginRedirect);

      const redirectUri = `${window.location.origin}/dashboard`;
      const authUrl = new URL(
        `${KEYCLOAK_BASE_URL}/realms/${encodeURIComponent(
          KEYCLOAK_REALM
        )}/protocol/openid-connect/auth`
      );

      authUrl.searchParams.set('client_id', KEYCLOAK_CLIENT_ID);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', 'openid profile email');

      window.location.assign(authUrl.toString());
    } catch (error) {
      console.error('Keycloak login error:', error);
      setError('Could not reach Keycloak. Please try again.');
    }
  };

  const handleKeycloakSignup = () => {
    setError('');
    try {
      const postLoginRedirect = returnTo || location.state?.from?.pathname || '/dashboard';
      sessionStorage.setItem('post_login_redirect', postLoginRedirect);

      const redirectUri = `${window.location.origin}/dashboard`;
      const signupUrl = new URL(
        `${KEYCLOAK_BASE_URL}/realms/${encodeURIComponent(
          KEYCLOAK_REALM
        )}/protocol/openid-connect/registrations`
      );

      signupUrl.searchParams.set('client_id', KEYCLOAK_CLIENT_ID);
      signupUrl.searchParams.set('redirect_uri', redirectUri);
      signupUrl.searchParams.set('response_type', 'code');
      signupUrl.searchParams.set('scope', 'openid profile email');

      window.location.assign(signupUrl.toString());
    } catch (error) {
      console.error('Keycloak signup error:', error);
      setError('Could not reach Keycloak signup. Please try again.');
    }
  };

  return (
    <div className="login-page">
      {/* 🎥 Background video */}
      <video autoPlay loop muted playsInline className="background-video">
        <source src="/videos/bg-video.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <div className="login-content">
        <div className="login-form">
          <div className="welcome-section">
            <img 
              src="/serventica-logo.svg" 
              alt="Servantica" 
              className="company-logo"
            />
            <h2 className="welcome-heading">Welcome to Servantica</h2>
            <p className="fade-in-subtitle">
              Your all-in-one digital workspace
            </p>

            {showCustomLogin ? (
              <>
                {error && <div className="error-message">{error}</div>}
                <button
                  onClick={handleAuth0Google}
                  className="google-button"
                  type="button"
                >
                  Continue with Google
                </button>
                <button
                  onClick={handleAuth0Google}
                  className="google-button"
                  type="button"
                >
                  Login or Signup with Google
                </button>
                <button
                  onClick={handleKeycloakLogin}
                  className="keycloak-button"
                  type="button"
                >
                  Sign in with Keycloak
                </button>
                <button
                  onClick={handleKeycloakSignup}
                  className="keycloak-button"
                  type="button"
                >
                  Create account with Keycloak
                </button>
              </>
            ) : (
              <div className="auth0-section">
                <p>Redirecting for secure authentication...</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;